import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Eye, FileText, Copy, X, ArrowsClockwise } from "@phosphor-icons/react";

export default function AdminTemplates() {
    const [templates, setTemplates] = useState([]);
    const [category, setCategory] = useState("");
    const [preview, setPreview] = useState(null);
    const [reseeding, setReseeding] = useState(false);

    const reseedSystem = async () => {
        if (!window.confirm("Refresh the 12 service + 6 generic system templates with the latest copy?\n\nYour custom (non-system) templates will not be touched.")) return;
        setReseeding(true);
        try {
            const { data } = await api.post("/admin/templates/reseed-system");
            toast.success(`Reseeded · ${data.inserted} system templates rebuilt`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reseed failed");
        } finally { setReseeding(false); }
    };

    const load = useCallback(async () => {
        const { data } = await api.get("/admin/templates", { params: category ? { category } : {} });
        setTemplates(data.items);
    }, [category]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => { document.title = "Surveys2CAD | Templates"; }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this template?")) return;
        try {
            await api.delete(`/admin/templates/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    const duplicate = async (t) => {
        try {
            await api.post("/admin/templates", {
                name: `${t.name} (copy)`,
                category: t.category,
                subject: t.subject,
                html_body: t.html_body,
                text_body: t.text_body || "",
                tags: t.tags || [],
            });
            toast.success("Duplicated");
            load();
        } catch {
            toast.error("Failed");
        }
    };

    const categories = ["", "service", "welcome", "follow-up", "newsletter", "case-study", "promo", "re-engagement", "general"];
    const categoryLabels = { "": "All", service: "Services", welcome: "Welcome", "follow-up": "Follow-up", newsletter: "Newsletter", "case-study": "Case Studies", promo: "Promo", "re-engagement": "Re-engagement", general: "General" };

    return (
        <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-templates">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="overline mb-2">Library</div>
                    <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-50 tracking-tight">Email Templates</h1>
                    <p className="text-slate-400 mt-2">{templates.length} templates · 12 service intros + 6 generic seeded</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={reseedSystem}
                        disabled={reseeding}
                        className="btn-ghost !border-orange-500/40 !text-orange-200 hover:!bg-orange-500/10 disabled:opacity-50"
                        data-testid="reseed-templates-btn"
                        title="Wipe & rebuild the 18 built-in system templates with the latest content. Your custom templates are untouched."
                    >
                        <ArrowsClockwise size={16} weight={reseeding ? "fill" : "bold"} className={reseeding ? "animate-spin" : ""} />
                        {reseeding ? "Reseeding…" : "Refresh system templates"}
                    </button>
                    <Link to="/admin/templates/new" className="btn-primary" data-testid="new-template-btn">
                        <Plus size={16} weight="bold" /> New template
                    </Link>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6" data-testid="template-categories">
                {categories.map((c) => (
                    <button
                        key={c || "all"}
                        onClick={() => setCategory(c)}
                        className={`px-3 py-1.5 rounded-full text-xs font-heading font-medium transition-colors ${
                            category === c
                                ? "bg-cyan-500/15 border border-cyan-400/50 text-cyan-200"
                                : "bg-slate-900/60 border border-slate-800 text-slate-400 hover:text-slate-200"
                        }`}
                        data-testid={`tpl-cat-${c || "all"}`}
                    >
                        {categoryLabels[c]}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((t) => (
                    <div key={t.id} className="tech-card p-5" data-testid={`template-${t.id}`}>
                        <div className="flex items-start justify-between mb-3">
                            <div className="overline">{t.category}{t.is_system && " · SYSTEM"}</div>
                            <FileText size={18} className="text-cyan-400" />
                        </div>
                        <h3 className="font-heading font-semibold text-slate-50 text-base mb-2">{t.name}</h3>
                        <p className="text-xs text-slate-400 line-clamp-2">{t.subject}</p>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
                            <div className="flex gap-1">
                                <button onClick={() => setPreview(t)} className="p-2 text-slate-400 hover:text-cyan-300" title="Preview" data-testid={`preview-${t.id}`}>
                                    <Eye size={16} />
                                </button>
                                <button onClick={() => duplicate(t)} className="p-2 text-slate-400 hover:text-cyan-300" title="Duplicate">
                                    <Copy size={16} />
                                </button>
                                {!t.is_system && (
                                    <>
                                        <Link to={`/admin/templates/${t.id}`} className="p-2 text-slate-400 hover:text-cyan-300" title="Edit">
                                            <PencilSimple size={16} />
                                        </Link>
                                        <button onClick={() => remove(t.id)} className="p-2 text-slate-400 hover:text-red-400" title="Delete">
                                            <Trash size={16} />
                                        </button>
                                    </>
                                )}
                            </div>
                            <Link to={`/admin/campaigns/new?template=${t.id}`} className="text-xs text-cyan-300 font-heading font-medium hover:underline" data-testid={`use-${t.id}`}>
                                Use in campaign →
                            </Link>
                        </div>
                    </div>
                ))}
            </div>

            {preview && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="template-preview-modal">
                    <div className="tech-card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-slate-800">
                            <div>
                                <div className="overline">{preview.category}</div>
                                <h3 className="font-heading text-lg font-semibold text-slate-50">{preview.name}</h3>
                                <div className="text-xs text-slate-400 mt-1">Subject: {preview.subject}</div>
                            </div>
                            <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-100">
                                <X size={22} />
                            </button>
                        </div>
                        <div className="p-5 flex-1 overflow-auto bg-slate-950/60">
                            <iframe
                                title="preview"
                                srcDoc={preview.html_body}
                                className="w-full h-[65vh] rounded-xl bg-white"
                                sandbox=""
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
